import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { toast } from "sonner";
import { 
  Users, 
  Package, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye,
  Send,
  FileText,
  Building,
  Phone,
  Mail,
  DollarSign,
  Rocket
} from "lucide-react";

const applicationStatusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  interviewing: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const applicationStatusLabels = {
  pending: "Pendiente",
  interviewing: "En entrevista",
  approved: "Aprobada",
  rejected: "Rechazada",
};

const productStatusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  published: "bg-blue-100 text-blue-800",
};

const productStatusLabels = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  published: "Publicado",
};

export default function AdminPartners() {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerProducts, setPartnerProducts] = useState([]);
  
  // Modals
  const [viewAppModal, setViewAppModal] = useState({ open: false, app: null });
  const [createPartnerModal, setCreatePartnerModal] = useState({ open: false, app: null, cif: "", address: "", commission: "10" });
  const [approveProductModal, setApproveProductModal] = useState({ open: false, product: null, finalPrice: "" });
  const [rejectProductModal, setRejectProductModal] = useState({ open: false, product: null, reason: "" });
  const [publishProductModal, setPublishProductModal] = useState({ 
    open: false, 
    product: null, 
    minParticipants: "5",
    maxParticipants: "20",
    timeWindowHours: "48",
    estimatedDeliveryDays: "10",
    confirmationThreshold: "80"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, partnersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/partner-applications`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/partner-products`)
      ]);
      setApplications(appsRes.data);
      setPartners(partnersRes.data);
      setPartnerProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching partner data:", error);
      toast.error("Error al cargar datos de partners");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAppStatus = async (appId, status, notes = null) => {
    try {
      let url = `${API}/admin/partner-applications/${appId}/status?status=${status}`;
      if (notes) url += `&notes=${encodeURIComponent(notes)}`;
      await axios.put(url);
      toast.success(`Estado actualizado a "${applicationStatusLabels[status]}"`);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleCreatePartner = async () => {
    const { app, cif, address, commission } = createPartnerModal;
    if (!cif || !address) {
      toast.error("CIF y dirección son obligatorios");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/partners?application_id=${app.application_id}&cif=${encodeURIComponent(cif)}&address=${encodeURIComponent(address)}&commission_rate=${commission}`);
      toast.success("Cuenta de partner creada");
      setCreatePartnerModal({ open: false, app: null, cif: "", address: "", commission: "10" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear partner");
    }
  };

  const handleApproveProduct = async () => {
    const { product, finalPrice } = approveProductModal;
    if (!finalPrice) {
      toast.error("Debes establecer un precio de venta");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/partner-products/${product.product_id}/approve?final_retail_price=${finalPrice}`);
      toast.success("Producto aprobado");
      setApproveProductModal({ open: false, product: null, finalPrice: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRejectProduct = async () => {
    const { product, reason } = rejectProductModal;
    if (!reason) {
      toast.error("Debes proporcionar un motivo");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/partner-products/${product.product_id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Producto rechazado");
      setRejectProductModal({ open: false, product: null, reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar");
    }
  };

  const handlePublishProduct = async () => {
    const { product, minParticipants, maxParticipants, timeWindowHours, estimatedDeliveryDays, confirmationThreshold } = publishProductModal;
    
    try {
      await axios.post(`${API}/admin/partner-products/${product.product_id}/publish`, null, {
        params: {
          min_participants: parseInt(minParticipants),
          max_participants: parseInt(maxParticipants),
          time_window_hours: parseInt(timeWindowHours),
          estimated_delivery_days: parseInt(estimatedDeliveryDays),
          confirmation_threshold: parseInt(confirmationThreshold)
        }
      });
      toast.success("Producto publicado como oferta");
      setPublishProductModal({ 
        open: false, 
        product: null, 
        minParticipants: "5",
        maxParticipants: "20",
        timeWindowHours: "48",
        estimatedDeliveryDays: "10",
        confirmationThreshold: "80"
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al publicar");
    }
  };

  const handleNotifyShipment = async (productId) => {
    try {
      await axios.post(`${API}/admin/partner-products/${productId}/notify-shipment`);
      toast.success("Notificación enviada al partner");
    } catch (error) {
      toast.error("Error al notificar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const pendingApps = applications.filter(a => a.status === "pending" || a.status === "interviewing");
  const pendingProducts = partnerProducts.filter(p => p.status === "pending");
  const approvedProducts = partnerProducts.filter(p => p.status === "approved");

  return (
    <div className="space-y-6" data-testid="admin-partners">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Solicitudes pendientes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingApps.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Partners activos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{partners.filter(p => p.status === "active").length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Productos por revisar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingProducts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Listos para publicar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{approvedProducts.length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="products" className="space-y-4">
        <TabsList data-testid="partner-admin-tabs">
          <TabsTrigger value="products">
            Productos ({partnerProducts.length})
          </TabsTrigger>
          <TabsTrigger value="applications">
            Solicitudes ({applications.length})
          </TabsTrigger>
          <TabsTrigger value="partners">
            Partners ({partners.length})
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Productos de Partners</CardTitle>
              <CardDescription>Revisa, aprueba y publica productos de tus partners</CardDescription>
            </CardHeader>
            <CardContent>
              {partnerProducts.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay productos de partners</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Coste Partner</TableHead>
                      <TableHead>Precio Venta</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partnerProducts.map(product => (
                      <TableRow key={product.product_id} data-testid={`partner-product-${product.product_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <img 
                              src={product.product_image} 
                              alt={product.product_name}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                            <div>
                              <p className="font-medium">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">{product.product_category} · {product.product_condition}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{product.partner?.company_name}</p>
                          <p className="text-xs text-muted-foreground">{product.partner?.commission_rate}% comisión</p>
                        </TableCell>
                        <TableCell>{product.partner_cost}€</TableCell>
                        <TableCell>
                          {product.final_retail_price ? (
                            <span className="font-medium">{product.final_retail_price}€</span>
                          ) : (
                            <span className="text-muted-foreground">{product.suggested_retail_price}€ (sugerido)</span>
                          )}
                        </TableCell>
                        <TableCell>{product.stock_quantity}</TableCell>
                        <TableCell>
                          <Badge className={productStatusColors[product.status]}>
                            {productStatusLabels[product.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {product.status === "pending" && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setApproveProductModal({ 
                                    open: true, 
                                    product, 
                                    finalPrice: String(product.suggested_retail_price) 
                                  })}
                                  data-testid={`approve-product-${product.product_id}`}
                                >
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setRejectProductModal({ open: true, product, reason: "" })}
                                  data-testid={`reject-product-${product.product_id}`}
                                >
                                  <XCircle className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {product.status === "approved" && (
                              <Button 
                                size="sm"
                                onClick={() => setPublishProductModal({ 
                                  ...publishProductModal,
                                  open: true, 
                                  product,
                                  maxParticipants: String(product.stock_quantity)
                                })}
                                data-testid={`publish-product-${product.product_id}`}
                              >
                                <Rocket className="h-4 w-4 mr-1" />
                                Publicar
                              </Button>
                            )}
                            {product.status === "published" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleNotifyShipment(product.product_id)}
                                data-testid={`notify-shipment-${product.product_id}`}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications">
          <Card>
            <CardHeader>
              <CardTitle>Solicitudes de Partners</CardTitle>
              <CardDescription>Gestiona las solicitudes de empresas que quieren ser partners</CardDescription>
            </CardHeader>
            <CardContent>
              {applications.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay solicitudes</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Productos</TableHead>
                      <TableHead>Volumen</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map(app => (
                      <TableRow key={app.application_id} data-testid={`application-${app.application_id}`}>
                        <TableCell>
                          <p className="font-medium">{app.company_name}</p>
                          {app.website && (
                            <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                              {app.website}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          <p>{app.contact_name}</p>
                          <p className="text-xs text-muted-foreground">{app.email}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {app.product_types?.map(type => (
                              <Badge key={type} variant="secondary" className="text-xs">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{app.estimated_monthly_volume}</TableCell>
                        <TableCell>
                          <Badge className={applicationStatusColors[app.status]}>
                            {applicationStatusLabels[app.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setViewAppModal({ open: true, app })}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {app.status === "pending" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleUpdateAppStatus(app.application_id, "interviewing")}
                              >
                                <Clock className="h-4 w-4 text-blue-500" />
                              </Button>
                            )}
                            {(app.status === "pending" || app.status === "interviewing") && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleUpdateAppStatus(app.application_id, "approved")}
                                >
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleUpdateAppStatus(app.application_id, "rejected")}
                                >
                                  <XCircle className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {app.status === "approved" && !partners.find(p => p.application_id === app.application_id) && (
                              <Button 
                                size="sm"
                                onClick={() => setCreatePartnerModal({ 
                                  open: true, 
                                  app, 
                                  cif: "", 
                                  address: "", 
                                  commission: "10" 
                                })}
                              >
                                Crear cuenta
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partners Tab */}
        <TabsContent value="partners">
          <Card>
            <CardHeader>
              <CardTitle>Partners Activos</CardTitle>
              <CardDescription>Gestiona tus partners y sus comisiones</CardDescription>
            </CardHeader>
            <CardContent>
              {partners.length === 0 ? (
                <div className="text-center py-12">
                  <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay partners registrados</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>CIF</TableHead>
                      <TableHead>Comisión</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Productos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map(partner => {
                      const productCount = partnerProducts.filter(p => p.partner_id === partner.partner_id).length;
                      return (
                        <TableRow key={partner.partner_id} data-testid={`partner-${partner.partner_id}`}>
                          <TableCell>
                            <p className="font-medium">{partner.company_name}</p>
                            {partner.website && (
                              <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                {partner.website}
                              </a>
                            )}
                          </TableCell>
                          <TableCell>
                            <p>{partner.contact_name}</p>
                            <p className="text-xs text-muted-foreground">{partner.email}</p>
                            <p className="text-xs text-muted-foreground">{partner.phone}</p>
                          </TableCell>
                          <TableCell>{partner.cif}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{partner.commission_rate}%</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={partner.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                              {partner.status === "active" ? "Activo" : "Inactivo"}
                            </Badge>
                          </TableCell>
                          <TableCell>{productCount}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Application Modal */}
      <Dialog open={viewAppModal.open} onOpenChange={(open) => !open && setViewAppModal({ open: false, app: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Solicitud</DialogTitle>
          </DialogHeader>
          {viewAppModal.app && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Empresa</p>
                  <p className="font-medium">{viewAppModal.app.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contacto</p>
                  <p className="font-medium">{viewAppModal.app.contact_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{viewAppModal.app.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Teléfono</p>
                  <p className="font-medium">{viewAppModal.app.phone}</p>
                </div>
              </div>
              {viewAppModal.app.website && (
                <div>
                  <p className="text-sm text-muted-foreground">Web</p>
                  <a href={viewAppModal.app.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {viewAppModal.app.website}
                  </a>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Productos</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {viewAppModal.app.product_types?.map(type => (
                    <Badge key={type} variant="secondary">{type}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Volumen estimado</p>
                <p className="font-medium">{viewAppModal.app.estimated_monthly_volume} unidades/mes</p>
              </div>
              {viewAppModal.app.message && (
                <div>
                  <p className="text-sm text-muted-foreground">Mensaje</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{viewAppModal.app.message}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Fecha de solicitud</p>
                <p className="font-medium">{new Date(viewAppModal.app.created_at).toLocaleDateString('es-ES')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Partner Modal */}
      <Dialog open={createPartnerModal.open} onOpenChange={(open) => !open && setCreatePartnerModal({ open: false, app: null, cif: "", address: "", commission: "10" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Cuenta de Partner</DialogTitle>
            <DialogDescription>
              Completa los datos fiscales para crear la cuenta de {createPartnerModal.app?.company_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>CIF/NIF *</Label>
              <Input
                value={createPartnerModal.cif}
                onChange={(e) => setCreatePartnerModal(prev => ({ ...prev, cif: e.target.value }))}
                placeholder="B12345678"
                data-testid="partner-cif-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección fiscal *</Label>
              <Input
                value={createPartnerModal.address}
                onChange={(e) => setCreatePartnerModal(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Calle, número, ciudad, CP"
                data-testid="partner-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Comisión BulkBytes (%)</Label>
              <Input
                type="number"
                value={createPartnerModal.commission}
                onChange={(e) => setCreatePartnerModal(prev => ({ ...prev, commission: e.target.value }))}
                min="1"
                max="50"
                data-testid="partner-commission-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePartnerModal({ open: false, app: null, cif: "", address: "", commission: "10" })}>
              Cancelar
            </Button>
            <Button onClick={handleCreatePartner} data-testid="confirm-create-partner">
              Crear cuenta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Product Modal */}
      <Dialog open={approveProductModal.open} onOpenChange={(open) => !open && setApproveProductModal({ open: false, product: null, finalPrice: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprobar Producto</DialogTitle>
            <DialogDescription>
              Establece el precio de venta final para {approveProductModal.product?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Coste partner</p>
                <p className="font-medium">{approveProductModal.product?.partner_cost}€</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Precio sugerido</p>
                <p className="font-medium">{approveProductModal.product?.suggested_retail_price}€</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Precio de venta final (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={approveProductModal.finalPrice}
                onChange={(e) => setApproveProductModal(prev => ({ ...prev, finalPrice: e.target.value }))}
                placeholder="600"
                data-testid="final-price-input"
              />
              <p className="text-xs text-muted-foreground">
                Este será el precio que vean los usuarios en BulkBytes
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveProductModal({ open: false, product: null, finalPrice: "" })}>
              Cancelar
            </Button>
            <Button onClick={handleApproveProduct} data-testid="confirm-approve-product">
              Aprobar producto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Product Modal */}
      <Dialog open={rejectProductModal.open} onOpenChange={(open) => !open && setRejectProductModal({ open: false, product: null, reason: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Producto</DialogTitle>
            <DialogDescription>
              Indica el motivo del rechazo para {rejectProductModal.product?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo del rechazo *</Label>
              <Input
                value={rejectProductModal.reason}
                onChange={(e) => setRejectProductModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Ej: Precio demasiado alto, fotos de baja calidad..."
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectProductModal({ open: false, product: null, reason: "" })}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleRejectProduct} data-testid="confirm-reject-product">
              Rechazar producto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Product Modal */}
      <Dialog open={publishProductModal.open} onOpenChange={(open) => !open && setPublishProductModal({ ...publishProductModal, open: false, product: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Publicar como Oferta</DialogTitle>
            <DialogDescription>
              Configura los parámetros de la oferta grupal para {publishProductModal.product?.product_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Precio de venta</span>
                <span className="font-medium">{publishProductModal.product?.final_retail_price}€</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Stock disponible</span>
                <span className="font-medium">{publishProductModal.product?.stock_quantity} unidades</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mínimo participantes</Label>
                <Input
                  type="number"
                  value={publishProductModal.minParticipants}
                  onChange={(e) => setPublishProductModal(prev => ({ ...prev, minParticipants: e.target.value }))}
                  data-testid="publish-min-participants"
                />
              </div>
              <div className="space-y-2">
                <Label>Máximo participantes</Label>
                <Input
                  type="number"
                  value={publishProductModal.maxParticipants}
                  onChange={(e) => setPublishProductModal(prev => ({ ...prev, maxParticipants: e.target.value }))}
                  data-testid="publish-max-participants"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duración (horas)</Label>
                <Select
                  value={publishProductModal.timeWindowHours}
                  onValueChange={(value) => setPublishProductModal(prev => ({ ...prev, timeWindowHours: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24">24 horas</SelectItem>
                    <SelectItem value="48">48 horas</SelectItem>
                    <SelectItem value="72">72 horas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Días de entrega</Label>
                <Input
                  type="number"
                  value={publishProductModal.estimatedDeliveryDays}
                  onChange={(e) => setPublishProductModal(prev => ({ ...prev, estimatedDeliveryDays: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Umbral de confirmación (%)</Label>
              <Input
                type="number"
                value={publishProductModal.confirmationThreshold}
                onChange={(e) => setPublishProductModal(prev => ({ ...prev, confirmationThreshold: e.target.value }))}
                min="50"
                max="100"
              />
              <p className="text-xs text-muted-foreground">
                % del mínimo para confirmar automáticamente al expirar
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishProductModal({ ...publishProductModal, open: false, product: null })}>
              Cancelar
            </Button>
            <Button onClick={handlePublishProduct} data-testid="confirm-publish-product">
              <Rocket className="h-4 w-4 mr-2" />
              Publicar oferta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
